import { lazy, Suspense, useState } from "react";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  Calculator,
  Calendar,
  ChevronRight,
  MapPin,
  Plane,
  PlusCircle,
} from "lucide-react";
import type { DashboardData, EntryPreset, Trip } from "../../../types";
import { money } from "../../../lib/finance";
import { Private, usePrivacy } from "../../../components/Privacy";
import { inferCurrencyForDestination } from "../../../lib/countries";
import { useTripStats } from "../../../hooks/use-trip-stats";
import { getDefaultTripAccount, getDefaultTripToAccount } from "../../../lib/trips";
import TripAccountsSpendBar from "../../trips/TripAccountsSpendBar";

const TripConverter = lazy(() => import("../../trips/TripConverter"));

/** "1 – 28 Oct", "28 Oct – 3 Nov", or with years when the trip spans two. */
function tripDateRange(start: string, end: string): string {
  const s = new Date(start + "T12:00:00");
  const e = new Date(end + "T12:00:00");
  const day = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric" });
  const dayMonth = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const full = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (s.getFullYear() !== e.getFullYear()) return `${full(s)} – ${full(e)}`;
  if (s.getMonth() === e.getMonth()) return `${day(s)} – ${dayMonth(e)}`;
  return `${dayMonth(s)} – ${dayMonth(e)}`;
}

export interface ActiveTripCardProps {
  trip: Trip;
  data: DashboardData;
  onAddTransaction?: (prefill: EntryPreset) => void;
  onOpenTrip?: (tripId: string) => void;
}

/** Prominent home screen card shown when a trip is active, featuring quick travel shortcuts (add expense, income, transfer, and converter). */
export default function ActiveTripCard({
  trip,
  data,
  onAddTransaction,
  onOpenTrip,
}: ActiveTripCardProps) {
  const [showConverter, setShowConverter] = useState(false);
  const { hidden } = usePrivacy();
  const stats = useTripStats(trip, data);
  const totalSpend = stats?.totalSpendBudget ?? 0;
  const budgetCurrency = stats?.budgetCurrency ?? data.baseCurrency;
  const budget = trip.budget;
  const budgetPercent = stats?.budgetPercent ?? 0;
  const isOver = stats?.isOverBudget ?? false;
  const remaining = stats?.remainingBudget ?? null;

  const destinationCurrencies = (trip.destinations || [])
    .map((d) => inferCurrencyForDestination(d))
    .filter((c): c is string => Boolean(c));

  const tripCurrencies = [
    ...destinationCurrencies,
    ...((stats?.walletBalances || []).map((w) => w.currency)),
    ...((stats?.tripTransactions || []).map((t) => t.currency)),
    trip.budgetCurrency || data.baseCurrency,
  ];

  // Currency is shown once beside the spend figure; every other number drops the suffix.
  const amount = (n: number) => money(n, budgetCurrency).replace(` ${budgetCurrency}`, "");
  const pacing = stats?.pacing;
  const destinations = (trip.destinations || []).join(", ");

  return (
    <section className="panel active-trip-module" aria-label={`Active trip: ${trip.name}`}>
      <button
        type="button"
        className="active-trip-header"
        onClick={() => onOpenTrip?.(trip.id)}
        disabled={!onOpenTrip}
        title="View trip details"
      >
        <span className="active-trip-title">
          <Plane size={15} className="active-trip-inline-icon" />
          <strong>{trip.name}</strong>
          {onOpenTrip && <ChevronRight size={16} className="active-trip-chevron" />}
        </span>
        <span className="active-trip-submeta">
          {destinations && (
            <span><MapPin size={11} />{destinations}</span>
          )}
          <span><Calendar size={11} />{tripDateRange(trip.startDate, trip.endDate)}</span>
          {pacing && (
            <span className="active-trip-day">
              {pacing.stage === "active"
                ? `Day ${pacing.currentDay}/${pacing.totalDays}`
                : `${pacing.totalDays} days`}
            </span>
          )}
        </span>
      </button>

      <div className="active-trip-figures">
        <div className="active-trip-figure">
          <span className="active-trip-label">Spent</span>
          <span className="active-trip-spend-val">
            <Private><strong>{amount(totalSpend)}</strong></Private>
            <small>{budgetCurrency}</small>
          </span>
        </div>
        {budget !== null && remaining !== null && (
          <div className={`active-trip-figure end ${isOver ? "over" : "under"}`}>
            <span className="active-trip-label">{isOver ? "Over" : "Left"}</span>
            <Private><strong>{amount(Math.abs(remaining))}</strong></Private>
          </div>
        )}
      </div>

      {budget !== null && !hidden && (
        <div className={`budget-bar-track ${isOver ? "over" : ""}`}>
          <div className="budget-bar-fill" style={{ width: `${Math.min(100, budgetPercent)}%` }} />
        </div>
      )}

      {(budget !== null || pacing) && (
        <div className="active-trip-pacing-strip">
          {budget !== null && (
            <span>Budget <strong><Private>{amount(budget)}</Private></strong></span>
          )}
          {pacing && (
            <span className={pacing.isPacingOver ? "warn" : ""}>
              Avg <strong><Private>{amount(Math.round(pacing.dailyAverageSpend))}</Private></strong>/day
            </span>
          )}
          {pacing && pacing.dailyBudgetRemaining !== null && pacing.stage === "active" && (
            <span className={isOver ? "over" : "good"}>
              Target <strong><Private>{amount(Math.round(pacing.dailyBudgetRemaining))}</Private></strong>/day
            </span>
          )}
        </div>
      )}

      {stats?.walletBalances && stats.walletBalances.length > 0 && !hidden && (
        <TripAccountsSpendBar
          walletBalances={stats.walletBalances}
          tripName={trip.name}
        />
      )}

      {/* Quick Action Shortcuts matching TripDetail */}
      <div className="account-quick-actions trip-actions-bar">
        <button
          type="button"
          onClick={() => {
            const defaultAcc = getDefaultTripAccount(trip, data.accounts, trip.destinations?.[0], data.trips, "Expense");
            onAddTransaction?.({
              tripId: trip.id,
              tripDestination: trip.destinations?.[0],
              type: "Expense",
              accountId: defaultAcc?.id,
            });
          }}
        >
          <PlusCircle size={16} />
          <span>Add expense</span>
        </button>

        <button
          type="button"
          onClick={() => {
            const defaultAcc = getDefaultTripAccount(trip, data.accounts, trip.destinations?.[0], data.trips, "Income");
            onAddTransaction?.({
              tripId: trip.id,
              tripDestination: trip.destinations?.[0],
              type: "Income",
              accountId: defaultAcc?.id,
            });
          }}
        >
          <ArrowDownLeft size={16} />
          <span>Add income</span>
        </button>

        <button
          type="button"
          onClick={() => {
            const defaultFrom = getDefaultTripAccount(trip, data.accounts, trip.destinations?.[0], data.trips, "Transfer");
            const defaultTo = getDefaultTripToAccount(trip, data.accounts, defaultFrom?.id, trip.destinations?.[0], data.trips);
            onAddTransaction?.({
              tripId: trip.id,
              tripDestination: trip.destinations?.[0],
              type: "Transfer",
              accountId: defaultFrom?.id,
              toAccountId: defaultTo?.id,
            });
          }}
        >
          <ArrowLeftRight size={16} />
          <span>Transfer / FX</span>
        </button>

        <button
          type="button"
          onClick={() => setShowConverter((s) => !s)}
          className={showConverter ? "active" : ""}
        >
          <Calculator size={16} />
          <span>Converter</span>
        </button>
      </div>

      {/* Inline Trip Converter / Calculator */}
      {showConverter && (
        <Suspense fallback={null}>
          <TripConverter
            baseCurrency={data.baseCurrency}
            tripCurrencies={tripCurrencies}
            destinations={trip.destinations}
            rates={data.rates}
          />
        </Suspense>
      )}
    </section>
  );
}
